import { forwardRef } from 'react';
import type { CSSProperties } from 'react';
import type { ForwardedRef, ReactElement } from 'react';
import type { Graph3DData } from '../core/graph.types';
import type { Graph3DLayoutConfig } from '../core/layout.types';
import type {
  Graph3DEventHandlers,
  Graph3DEdgeRendererRegistry,
  Graph3DHandle,
  Graph3DInteractionConfig,
  Graph3DNodeRendererRegistry,
} from '../core/renderer.types';
import type { Graph3DRuntime } from '../core/runtime.types';
import type { Graph3DTheme } from '../core/theme.types';
import { defaultGraph3DTheme } from '../theme/defaultTheme';
import { Graph3DCanvas } from './Graph3DCanvas';

type Graph3DThemeOverrides = Partial<
  Omit<Graph3DTheme, 'node' | 'edge' | 'runtime'>
> & {
  node?: Partial<Graph3DTheme['node']>;
  edge?: Partial<Graph3DTheme['edge']>;
  runtime?: Partial<Graph3DTheme['runtime']>;
};

export interface Graph3DProps<TNodeData = unknown, TEdgeData = unknown> {
  data: Graph3DData<TNodeData, TEdgeData>;
  runtime?: Graph3DRuntime;
  layout?: Graph3DLayoutConfig;
  theme?: Graph3DThemeOverrides;
  interactions?: Graph3DInteractionConfig;
  nodeRenderers?: Graph3DNodeRendererRegistry<TNodeData>;
  edgeRenderers?: Graph3DEdgeRendererRegistry<TEdgeData, TNodeData>;
  events?: Graph3DEventHandlers<TNodeData, TEdgeData>;
  showInteractionHint?: boolean;
  style?: CSSProperties;
  className?: string;
}

export const Graph3D = forwardRef(function Graph3DInner<
  TNodeData = unknown,
  TEdgeData = unknown,
>(
  {
    data,
    runtime,
    layout,
    theme,
    interactions,
    nodeRenderers,
    edgeRenderers,
    events,
    showInteractionHint,
    style,
    className,
  }: Graph3DProps<TNodeData, TEdgeData>,
  ref: ForwardedRef<Graph3DHandle>
) {
  const mergedTheme: Graph3DTheme = {
    ...defaultGraph3DTheme,
    ...theme,
    node: { ...defaultGraph3DTheme.node, ...theme?.node },
    edge: { ...defaultGraph3DTheme.edge, ...theme?.edge },
    runtime: { ...defaultGraph3DTheme.runtime, ...theme?.runtime },
  };

  return (
    <Graph3DCanvas
      ref={ref}
      data={data}
      runtime={runtime}
      layout={layout}
      theme={mergedTheme}
      interactions={interactions}
      nodeRenderers={nodeRenderers}
      edgeRenderers={edgeRenderers}
      events={events}
      showInteractionHint={showInteractionHint}
      style={style}
      className={className}
    />
  );
}) as <TNodeData = unknown, TEdgeData = unknown>(
  props: Graph3DProps<TNodeData, TEdgeData> & {
    ref?: ForwardedRef<Graph3DHandle>;
  }
) => ReactElement;
